import type { ReactNode } from 'react'
import type { Role } from '@lms/shared-types'
import { Icon } from '../primitives/Icon'
import { useOrgConfig } from '../../lib/org-config-context'
import { flags } from '../../lib/feature-flags'
import { NotificationsBell } from './NotificationsBell'
import { ProfileMenu } from './ProfileMenu'

/**
 * The full-width top chrome of the application shell (60px).
 *
 * Layout (updated 2026-07): the top bar spans the ENTIRE width — the sidebar
 * now starts *below* it — and carries the brand on the left, so the gradient
 * reads as one clean masthead instead of a band notched around the sidebar.
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ ✳  YTL AI LABS            gradient        🔔  JD Jane ▾  │
 *   │    Leave Management System                               │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Chrome rules:
 *   - Background: navy→azure brand gradient (`TOPBAR_GRADIENT`).
 *   - Foreground (brand, bell, profile) is white via `onDark` so it reads on
 *     the gradient; the bell/profile DROPDOWN panels keep their light surface —
 *     only the triggers go white.
 *   - Left: the brand only — logo + two-line lockup (company eyebrow over the
 *     product name), linking Home. The sidebar owns its own collapse toggle.
 *     (Mobile: a hamburger replaces the sidebar and opens the drawer.)
 *   - Right: Viewing-As pill · notifications · profile.
 *   - Status colors only on chips/pills, NEVER on chrome surfaces.
 */

export interface TopBarProps {
  userName: string
  userEmail: string
  role: Role
  /** Full role set — passed to ProfileMenu as the identity-badge fallback. */
  roles?: Role[]
  /** The user's team name — shown as the ProfileMenu identity badge in place
   *  of the role. Sourced from GET /api/v1/me by AppShell. */
  teamName?: string | null
  onSignOut: () => void
  /** Whether the user has a local password (forwarded to ProfileMenu). SSO
   *  users have none, so the Account item is hidden for them. */
  hasLocalPassword?: boolean | undefined
  /** When provided, shows the mobile hamburger button that opens the
   *  MobileDrawer (mobile only). */
  onOpenMobileDrawer?: () => void
  /** Viewport flags from the parent — control which trigger renders, plus
   *  the responsive shape of the identity block. */
  isMobile?: boolean
  isTablet?: boolean
  /** Optional slot rendered immediately to the left of the identity block
   *  (desktop/tablet only). Used by AppShell to host the "Viewing As" pill
   *  for HR+ users when the feature flag is on. */
  viewingAs?: ReactNode
}

export const HEADER_HEIGHT = 68

// Brand gradient for the top chrome — navy anchored at the left, brightening
// to a vivid azure on the right (matches the approved masthead design).
// Foreground (bell, profile, brand) flips to white via `onDark` so it reads.
const TOPBAR_GRADIENT =
  'linear-gradient(100deg, #0e2a5e 0%, #1b48a6 55%, #3f86ea 100%)'

export function TopBar({
  userName,
  userEmail,
  role,
  roles,
  teamName,
  onSignOut,
  hasLocalPassword,
  onOpenMobileDrawer,
  isMobile = false,
  isTablet = false,
  viewingAs,
}: Readonly<TopBarProps>): JSX.Element {
  const org = useOrgConfig()
  const profileRoles: Role[] = roles && roles.length > 0 ? roles : [role]
  return (
    <header
      data-testid="topbar"
      style={{
        height: HEADER_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '0 12px' : '0 24px',
        background: TOPBAR_GRADIENT,
        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
        {/* Mobile: hamburger opens the drawer. Desktop/tablet: no control here —
            the sidebar owns its own collapse toggle; the top bar is just brand. */}
        {isMobile && onOpenMobileDrawer ? (
          <HamburgerButton onClick={onOpenMobileDrawer} />
        ) : null}

        {/* Brand — logo + two-line lockup: company eyebrow over the product
            name. Links Home. The masthead identity for every viewport. */}
        <a
          href="/"
          aria-label={`${org.appName} — home`}
          data-testid="topbar-brand"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            minWidth: 0,
            textDecoration: 'none',
          }}
        >
          <img
            src={org.logoDarkUrl ?? '/logo-dark.png'}
            alt=""
            aria-hidden
            style={{
              display: 'block',
              flexShrink: 0,
              height: 40,
              width: 'auto',
              maxWidth: 56,
              objectFit: 'contain',
              // Dark logo on the blue gradient — invert to read as white.
              filter: 'brightness(0) invert(1)',
            }}
          />
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.18 }}>
            <span
              style={{
                fontFamily: 'var(--font-mono, var(--font-sans))',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.11em',
                textTransform: 'uppercase',
                color: 'rgba(255, 255, 255, 0.72)',
                whiteSpace: 'nowrap',
              }}
            >
              {org.companyName}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'var(--surface)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {org.appName}
            </span>
          </span>
        </a>

      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isTablet ? 12 : 16,
          minWidth: 0,
        }}
      >
        {!isMobile && viewingAs ? viewingAs : null}
        {!isMobile ? (
          <NotificationsBell role={role} onDark />
        ) : null}
        {!isMobile && (
          <ProfileMenu
            userName={userName}
            userEmail={userEmail}
            roles={profileRoles}
            teamName={teamName}
            onSignOut={onSignOut}
            compact={isTablet}
            hasLocalPassword={hasLocalPassword}
            onDark
          />
        )}
      </div>
    </header>
  )
}

interface HamburgerButtonProps {
  onClick: () => void
}

interface PaletteSearchTriggerProps {
  isTablet?: boolean
}

/**
 * Wireframe-faithful search trigger in the TopBar — looks like an input
 * but is actually a button that fires the `lms:open-palette` event the
 * CommandPalette listens for. ⌘K opens it too; this is the visual
 * affordance for users who don't know the shortcut.
 *
 * Tablet variant collapses to icon-only to save horizontal space for
 * the role eyebrow + identity block.
 */
function PaletteSearchTrigger({ isTablet = false }: Readonly<PaletteSearchTriggerProps>): JSX.Element {
  const handleOpen = (): void => {
    window.dispatchEvent(new Event('lms:open-palette'))
  }
  if (isTablet) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Open command palette"
        data-testid="palette-search-trigger"
        style={{
          minWidth: 44,
          minHeight: 44,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          borderRadius: 8,
          color: 'var(--text-2)',
          cursor: 'pointer',
        }}
      >
        <Icon name="search" size={20} aria-hidden />
      </button>
    )
  }
  return (
    <button
      type="button"
      onClick={handleOpen}
      data-testid="palette-search-trigger"
      style={{
        flex: 1,
        maxWidth: 480,
        minHeight: 36,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 12px',
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        color: 'var(--text-3)',
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        textAlign: 'left',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--line-strong)'
        e.currentTarget.style.color = 'var(--text-2)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border)'
        e.currentTarget.style.color = 'var(--text-3)'
      }}
    >
      <Icon name="search" size={16} aria-hidden />
      <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Ask or jump to anything…
      </span>
      <kbd
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          padding: '2px 6px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 4,
          color: 'var(--text-3)',
          flexShrink: 0,
        }}
      >
        ⌘K
      </kbd>
    </button>
  )
}

function HamburgerButton({ onClick }: Readonly<HamburgerButtonProps>): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open navigation"
      data-testid="mobile-drawer-trigger"
      style={{
        minWidth: 44,
        minHeight: 44,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: 8,
        color: 'var(--surface)',
        cursor: 'pointer',
      }}
    >
      <Icon name="menu" size={22} aria-hidden />
    </button>
  )
}
